import React, { useMemo } from "react";
import "./styles.scss";
import {
  FlightAlertKey,
  getWatchDetails,
  ItineraryWithType,
  MyTripsModalTypes,
  Passengers,
  PassengerTypes,
  ShopFilter,
  SliceStopCountFilter,
  TripCategory,
  WatchAlertView,
  ItineraryEnum,
  SELECT_WATCHED_FLIGHT,
  SelectWatchedFlightProperties,
  getWatchFilterTypeFromShopFilter,
  APP_GTM_ENTRY,
} from "redmond";
import { Box, Typography } from "@material-ui/core";
import queryStringParser from "query-string";
import {
  TripInfoDetails,
  ActionLinks,
  MyTripsCard,
  MyTripsMobileCard,
  ActionButton,
  TravelAppBanner,
} from "halifax";
import { RouteComponentProps } from "react-router";
import dayjs from "dayjs";
import { getIsTravelApp } from "@capone/common";
import * as textConstants from "./constants";
import { PATH_FLIGHTS_SHOP } from "../../../../utils/paths";
import { WatchItineraryModal } from "./WatchItineraryModal";
// import { PriceDropProtectionLink } from "./PriceDropProtectionLink";
import { trackEvent } from "../../../../api/v1/analytics/trackEvent";
import { WatchListConnectorProps } from "./container";

import {
  ActiveExperiments,
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";

export interface IWatchListProps
  extends WatchListConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
}

export const addWatchType = (watch: WatchAlertView): ItineraryWithType => ({
  ...watch,
  type: ItineraryEnum.Watch,
});

export const WatchList = ({
  isMobile,
  watches,
  context,
  history,
  setOpenModal,
}: IWatchListProps) => {
  // TO DO implement change e-mail modal

  const { isTravelApp } = getIsTravelApp(history.location.search);

  const expState = useExperiments();

  const travelAppGTMExperiment = getExperimentVariant(
    expState.experiments,
    ActiveExperiments.TRAVEL_APP_GTM_EXPERIMENT,
  );
  const isTravelAppGTMExperiment = useMemo(
    () => travelAppGTMExperiment === AVAILABLE,
    [travelAppGTMExperiment],
  );

  const redirectToShop = (key: FlightAlertKey, passengers: Passengers) => {
    let stopsOption = SliceStopCountFilter.ANY_NUMBER;
    let noLCC = false;
    switch (key.filter) {
      case ShopFilter.NoLCC:
        noLCC = true;
        break;
      case ShopFilter.NonStop:
        stopsOption = SliceStopCountFilter.NONE;
        break;
      case ShopFilter.NonStopNoLCC:
        stopsOption = SliceStopCountFilter.NONE;
        noLCC = true;
        break;
      default:
        break;
    }
    const newQuery = {
      tripCategory: key.returnDate
        ? TripCategory.ROUND_TRIP
        : TripCategory.ONE_WAY,
      origin: key.origin.code,
      destination: key.destination.code,
      departureDate: key.departureDate,
      returnDate: key.returnDate,
      isFromFlightWatch: true,
      stopsOption,
      noLCC,
      isTravelApp: isTravelApp || undefined,
      adultsCount: passengers?.[PassengerTypes.Adult] ?? 1,
      childrenCount: passengers?.[PassengerTypes.Child] ?? 0,
      infantsInSeatCount: passengers?.[PassengerTypes.InfantInSeat] ?? 0,
      infantsOnLapCount: passengers?.[PassengerTypes.InfantInLap] ?? 0,
    };
    history.push({
      pathname: PATH_FLIGHTS_SHOP,
      search: queryStringParser.stringify(newQuery),
    });
  };

  const onEmailChangeClick = (watch: WatchAlertView) => {
    setOpenModal({
      type: MyTripsModalTypes.ChangeEmail,
      selectedItinerary: addWatchType(watch),
    });
  };

  const onStopWatchingClick = (watch: WatchAlertView) => {
    setOpenModal({
      type: MyTripsModalTypes.StopWatching,
      selectedItinerary: addWatchType(watch),
    });
  };

  const getActionsForWatch = (watch: WatchAlertView) => [
    { content: "Change e-mail", onClick: () => onEmailChangeClick(watch) },
    {
      content: "Stop watching this route",
      onClick: () => onStopWatchingClick(watch),
    },
  ];

  return (
    <Box className="watch-list">
      {watches.map((watch: WatchAlertView, index: number) => {
        const watchDetails = getWatchDetails(
          watch,
          context,
          (date: string) => dayjs(date).format(textConstants.DATE_FORMAT),
          textConstants.getFilterLabel,
          // textConstants.BOOK_NOW,
          // textConstants.WAIT
        );
        return isMobile ? (
          <MyTripsMobileCard
            key={index}
            confirmationLabel={textConstants.CONFIRMATION}
            label={textConstants.FLIGHT_WATCH}
            onClick={() => redirectToShop(watch.key.value, watch.passengers)}
            {...watchDetails}
            // Commented out until this is complete: https://hopper-jira.atlassian.net/browse/CMKT-4178
            // actions={
            //   <>
            //     <PriceDropProtectionLink
            //       priceDropProtection={watch.priceDropProtection}
            //       isMobile={true}
            //     />
            //     <ActionLinks actions={getActionsForWatch(watch)} />
            //   </>
            // }
          />
        ) : (
          <React.Fragment key={index}>
            <Typography variant="body1" className="summary-label">
              {`${textConstants.WATCH_HEADER} ${dayjs(
                watch.key.value.departureDate,
              ).format("MMM D, YYYY")}`}
            </Typography>
            <MyTripsCard
              content={
                <TripInfoDetails
                  titles={{
                    confirmationLabel: textConstants.CONFIRMATION,
                    startLabel: `${textConstants.DEPARTURE}:`,
                    endLabel: `${textConstants.RETURN}:`,
                    ...watchDetails,
                  }}
                />
              }
              actions={<ActionLinks actions={getActionsForWatch(watch)} />}
              cta={
                <Box className="ctas-section">
                  {/* <PriceDropProtectionLink
                    priceDropProtection={watch.priceDropProtection}
                    isMobile={false}
                  /> */}
                  <ActionButton
                    message={textConstants.VIEW_FLIGHTS}
                    defaultStyle="h4r-primary"
                    ariaLabelText={`${textConstants.VIEW_FLIGHTS} for ${watchDetails.title}`}
                    onClick={() => {
                      trackEvent({
                        eventName: SELECT_WATCHED_FLIGHT,
                        properties: {
                          advance: dayjs(watch.key.value.departureDate).diff(
                            dayjs(),
                            "days",
                          ),
                          departure_date: watch.key.value.departureDate,
                          return_date: watch.key.value.returnDate,
                          origin: watch.key.value.origin.code,
                          destination: watch.key.value.destination.code,
                          trip_type:
                            watch.key.value.returnDate !== undefined
                              ? TripCategory.ROUND_TRIP
                              : TripCategory.ONE_WAY,
                          filter_type: getWatchFilterTypeFromShopFilter(
                            watch.key.value.filter,
                          ),
                          number_of_active_watches: watches.length,
                        } as SelectWatchedFlightProperties,
                      });
                      redirectToShop(watch.key.value, watch.passengers);
                    }}
                  />
                </Box>
              }
            />
          </React.Fragment>
        );
      })}
      {!isTravelApp && isTravelAppGTMExperiment && (
        <TravelAppBanner
          isMobile={isMobile}
          onCTAClick={() => {
            trackEvent({
              eventName: APP_GTM_ENTRY,
              properties: {
                location: "my_trips",
              },
            });
          }}
        />
      )}
      <WatchItineraryModal isMobile={isMobile} />
    </Box>
  );
};
